import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { api } from "@/lib/api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// --- Warm-up ping ---
// Fires immediately on app load to wake the backend container if it cold-started.
// Then every 4 minutes while the tab is visible — well under typical idle-shutdown
// thresholds on most container platforms. Fail-silent: if the backend isn't reachable
// yet, we don't surface errors — the user's normal API call will retry.
function startWarmupPing() {
  const ping = () => api.get("/health").catch(() => {});
  ping();
  let timer = null;
  const start = () => { if (!timer) timer = setInterval(ping, 4 * 60 * 1000); };
  const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
  start();
  document.addEventListener("visibilitychange", () => {
    document.hidden ? stop() : (ping(), start());
  });
}
startWarmupPing();

// --- Splash fade-out ---
// Inline splash (#zidamd-splash in index.html) is visible from the first paint.
// We fade it out once React has mounted so users never see a blank white screen.
// Also clears the stuck-loader timer set in index.html so the "still loading" UI
// never appears once React is alive.
function dismissSplash() {
  if (window.__zidamdStuckTimer) {
    clearTimeout(window.__zidamdStuckTimer);
    window.__zidamdStuckTimer = null;
  }
  const el = document.getElementById("zidamd-splash");
  if (!el) return;
  el.classList.add("zidamd-fade-out");
  setTimeout(() => el.remove(), 400);
}

// --- Top-level error boundary ---
// Prevents a single render error from blanking the whole app. Critical on mobile
// Safari where unhandled exceptions during hydration leave a white screen.
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ZidaMD root error:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          minHeight: "100vh", padding: 24, display: "flex",
          alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg, #1a0a2e 0%, #2d1454 50%, #4a0e8f 100%)",
          color: "#fff", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
        }} data-testid="root-error">
          <div style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>ZidaMD</div>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 16 }}>
              Something went wrong loading the app.
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 20, fontFamily: "monospace" }}>
              {String(this.state.error?.message || this.state.error || "Unknown error")}
            </div>
            <button onClick={() => window.location.reload()} style={{
              padding: "10px 24px", background: "#c9a24b", color: "#1a0a2e",
              border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600,
              cursor: "pointer", minHeight: 44,
            }}>Reload app</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </RootErrorBoundary>
  </React.StrictMode>,
);

// Give React one tick to paint, then drop the splash.
requestAnimationFrame(() => requestAnimationFrame(dismissSplash));
